













































import { defineComponent, ref } from "@vue/composition-api";
import {
  Appointment,
  InputAppointmentSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { AppointmentTypeKind } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import BaseSpinner from "@/components/BaseSpinner";
import EmptyContent from "@/components/EmptyContent";
import InfiniteScroll from "@/components/InfiniteScroll";
import { AppointmentsTab, AppointmentsTabNameEnum } from "../../config";
import AppointmentAcceptConfirm from "../AppointmentAcceptConfirm";
import AppointmentAddToCalendarConfirm from "../AppointmentAddToCalendarConfirm";
import AppointmentCancelConfirm from "../AppointmentCancelConfirm";
import AppointmentDeclineConfirm from "../AppointmentDeclineConfirm";
import AppointmentsItem from "../AppointmentsItem";

export default defineComponent({
  name: "AppointmentsTabController",
  components: {
    AppointmentAddToCalendarConfirm,
    AppointmentCancelConfirm,
    AppointmentDeclineConfirm,
    AppointmentAcceptConfirm,
    AppointmentsItem,
    InfiniteScroll,
    EmptyContent,
    BaseSpinner,
  },
  props: {
    tab: { type: Object as () => AppointmentsTab, required: true },
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();
    const loading = ref(false);
    const appointmentToDecline = ref<Appointment | null>(null);
    const appointmentToAccept = ref<Appointment | null>(null);
    const appointmentToCancel = ref<Appointment | null>(null);
    const appointmentToCalendar = ref<Appointment | null>(null);

    const { items, data, getFirstPage, getNextPage, removeItem, updateItem, enableScroll } = usePager<
      Appointment,
      InputAppointmentSearch
    >((page, data) => {
      return providers.AppointmentProvider.search({
        page,
        sort: "START_TIME,ASC",
        ...props.tab.getData(authUserStrict.value),
        ...data,
      });
    });

    const accept = async (id: string, cb?: () => Promise<any>, processingCb?: () => void) => {
      try {
        await providers.AppointmentProvider.approve({ data: { id } });
        await removeItem(i => i.id === id);
        cb && (await cb());
        emit("updateTabsCount", data.value?.totalRecords);
        emit("accept");
      } finally {
        processingCb && processingCb();
        appointmentToAccept.value = null;
      }
    };

    const decline = async (message: string = "", cb?: () => void) => {
      if (!appointmentToDecline.value) return;
      const id = appointmentToDecline.value.id;
      try {
        await providers.AppointmentProvider.decline({ data: { id, message } });
        await removeItem(i => i.id === id);
        emit("updateTabsCount", data.value?.totalRecords);
        appointmentToDecline.value = null;
      } finally {
        cb && cb();
      }
    };

    const handleAccept = (id: string, item: Appointment, processingCb?: () => void) => {
      if (item.typeKind === AppointmentTypeKind.TOUR_GUIDE) {
        appointmentToAccept.value = item;
      } else {
        accept(id, undefined, processingCb);
      }
    };

    const cancel = async (message: string = "", cb?: () => void) => {
      if (!appointmentToCancel.value) return;
      const id = appointmentToCancel.value.id;
      try {
        const response = await providers.AppointmentProvider.cancel({ data: { id, message } });
        if (props.tab.name === AppointmentsTabNameEnum.PENDING) await removeItem(i => i.id === id);
        else updateItem(response, (i1, i2) => i1.id === i2.id);
        appointmentToCancel.value = null;
      } finally {
        cb && cb();
      }
    };

    const remove = async (id: string) => {
      try {
        loading.value = true;
        const response = await providers.AppointmentProvider.hide({ id });
        await removeItem(i => i.id === response.id);
      } finally {
        loading.value = false;
      }
    };

    const getData = async () => {
      try {
        loading.value = true;
        await getFirstPage();
        if (props.tab.name === AppointmentsTabNameEnum.REQUESTS) emit("updateTabsCount", data.value?.totalRecords);
        if (props.tab.name === AppointmentsTabNameEnum.UPCOMING_WITH_ME) emit("updateTabsCount", 0);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      loading,
      enableScroll,
      items,
      data,
      getNextPage,
      authUserStrict,
      appointmentToDecline,
      appointmentToAccept,
      appointmentToCancel,
      appointmentToCalendar,
      accept,
      handleAccept,
      decline,
      cancel,
      remove,
    };
  },
});
